import Typography from '@mui/material/Typography';
import AuraAutoComplete from 'app/main/apps/common/AuraAutoComplete';
import {
  shipToOptions,
  shouldOfferShipToFilter,
  resolveActiveShipTo,
} from 'app/main/apps/backoffice/shipments/shipToFilter';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from '@fuse/hooks';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { matchSorter } from 'match-sorter';
import { useEffect, useState, memo } from 'react';
import { useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import { getCustomersShipment } from 'app/main/apps/client/store/customersSlice';
import { getStatuses } from 'app/main/dashboards/main/store/widgetsSlice';
import { setOrderTypes, setSelectedValue } from '../store/ordersSlice';
import { getAvailableOrders, getCustomers, getOrderType, getOrderTypeLocations, resetEntities, selectOrders, setOrderTypeLocation, setShipToFilter, setSupplierOrderType, setWeightRange } from '../../backoffice/store/shipmentOrderSlice';
import { VAS_TYPE_CHOICES, formatVasTypesParam, isVasType, shouldShowOrderVasTypes, vasTypeFilterLabels } from './vasFilterChoices';
import { withoutKey } from 'app/main/apps/common/optionProps';

function CustomerFilter(props) {
  const { setCustomer, defaultCustomer, filterValue, setSupplier, isOrderModule, pageRef, autoCompleteSxStyle = null, pageType } = props;
  const dispatch = useDispatch();
  const currentUser = useSelector(({ user }) => user);
  const suppliersList = useSelector(state => state.eCommerceApp.orders.suppliersList);
  const selectedSupplier = useSelector(state => state.eCommerceApp.orders.supplier);
  const customersListItems = useSelector(state => state.eCommerceApp.orders.customersListItems);
  const { location } = window;
  const isCustomer = currentUser && currentUser.role === 'customer';
  const [customers, setCustomers] = useState([]);
  const isCustomerFilter = (!['supplier', 'supplier_archive',].includes(filterValue?.value)) || !isOrderModule;
  const sp = new URLSearchParams(location.search)
  useDeepCompareEffect(() => {
    if (!isCustomer && location.pathname === '/shipments/new') {
      dispatch(getCustomersShipment({ lt: 'shipment' })).then(({ payload }) => {
        setCustomers(payload.data);
      });
    } else if (!isCustomer) {
      if (customersListItems) {
        setCustomers(customersListItems);
      }
    }
  }, [dispatch, isCustomer, location.pathname, customersListItems]);

  useEffect(() => {
    if (location.search && isOrderModule && !pageRef?.current) {
      const cid = sp.get('cid');
      const sid = sp.get('sid');
      const orderType = sp.get('o_type')
      if (cid && customers?.length) {
        const customer = customers.find(c => c.id === cid)
        dispatch(setCustomer(customer));
        pageRef.current = true
      }
      else if (sid && suppliersList?.length) {
        const supplier = suppliersList.find(c => c.id === sid)
        dispatch(setSupplier(supplier));
        pageRef.current = true
      }
    }
  }, [location, customers, suppliersList, dispatch, setCustomer, setSupplier, isOrderModule])

  const handleCustomerChange = (value) => {
    sp.delete('sid')
    sp.delete('cid')
    if (!value && filterValue?.value.includes('supplier'))
      sp.set('sid', 'all');

    if (isCustomerFilter) {
      dispatch(setCustomer(value));
      if (isOrderModule) {
        dispatch(setSupplier(null));
        if (value) {
          sp.set('cid', value.id)
        }
      }
    }
    else {
      dispatch(setSupplier(value));
      dispatch(setCustomer(null));
      if (value)
        sp.set('sid', value.id)
    }

    if (isOrderModule)
      window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  };

  const inputValues = isCustomerFilter ? customers : [{ name: 'All', value: 'all', email: '', id: 'all' }, ...suppliersList]
  const defaultValue = isCustomerFilter ? defaultCustomer : selectedSupplier

  return (
    <div className={`${pageType === 'order' ? "flex flex-col mx-6" : "flex flex-row mx-6"}`}>
      <Typography className={`"text-12 md:text-14 pr-8 " ${pageType === 'order' ? "pt-4" : "pt-12"}`}>{isCustomerFilter ? 'Customer' : 'Supplier'}:</Typography>
      <div className="text-12 md:text-14 pr-8 font-semibold">
        <AuraAutoComplete
          placeholder={`All ${isCustomerFilter ? 'Customers' : 'Suppliers'}`}
          inputValues={inputValues}
          onValueChange={handleCustomerChange}
          defaultValue={defaultValue || null}
          optionDisplayName="name"
          // isOptionEqualToValue={(option, value) => option.name === value.name}
          isOptionEqualToValue={(option, value1) => {
            if (option && option.name && value1.name) {
              return option.name === value1.name;
            }
            return false;
          }}
          hideClearable={false}
          renderOption={(renderprops, option) => (
            <Box
              component="li"
              {...withoutKey(renderprops)}
              key={option.id}
              className={`${renderprops.className} !block py-2 px-10`}
            >
              <div className="font-medium">{option.name}</div>
              <div className="font-light text-10 text-gray-400">{option.email}</div>
            </Box>
          )}
          filterOptions={(options, { inputValue }) =>
            matchSorter(options, inputValue, {
              keys: ['name'],
              threshold: matchSorter.rankings.STARTS_WITH,
            })
          }
          sxStyle={autoCompleteSxStyle}
        />
      </div>
    </div>
  );
}

export function SalesPersonFilter(props) {
  const { setSalesPerson, defaultSalesPerson, filterValue, isOrderModule, pageRef, autoCompleteSxStyle = null, pageType } = props;
  const dispatch = useDispatch();
  const { location } = window;
  const salesPersonListItems = useSelector(state => state.eCommerceApp.orders.salesPersonListItems);
  const [salesPersons, setSalesPersons] = useState([]);
  const isCustomerFilter = (!['supplier', 'supplier_archive',].includes(filterValue?.value)) || !isOrderModule;
  const sp = new URLSearchParams(location.search)

  useDeepCompareEffect(() => {
    if (salesPersonListItems) {
      setSalesPersons(salesPersonListItems);
    }
  }, [salesPersonListItems]);

  useEffect(() => {
    if (location.search && isOrderModule && !pageRef?.current) {
      const spid = sp.get('sales_person');
      if (spid && salesPersons?.length) {
        const salesPerson = salesPersons.find(s => s.id === spid)
        dispatch(setSalesPerson(salesPerson));
        pageRef.current = true
      }
    }
  }, [location, salesPersons, dispatch, setSalesPerson, isOrderModule])

  const handleSalesPersonChange = (value) => {
    sp.delete('sales_person');
    if (isCustomerFilter) {
      dispatch(setSalesPerson(value));
      if (isOrderModule && value && value.id) {
        sp.set('sales_person', value.id);
      }
    }
    if (isOrderModule)
      window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  };

  const inputValues = salesPersons
  const defaultValue = defaultSalesPerson
  return (
    <div className={`${pageType === 'order' ? "flex flex-col mx-6" : "flex flex-row mx-6"}`}>
      <Typography className={`"text-12 md:text-14 pr-8 " ${pageType === 'order' ? "pt-4" : "pt-12"}`}>Sales Person:</Typography>
      <div className="text-12 md:text-14 pr-8 font-semibold">
        <AuraAutoComplete
          placeholder="All Sales Persons"
          inputValues={inputValues}
          onValueChange={handleSalesPersonChange}
          defaultValue={defaultValue || null}
          optionDisplayName="name"
          // isOptionEqualToValue={(option, value) => option.name === value.name}
          isOptionEqualToValue={(option, value1) => {
            if (option && option.name && value1.name) {
              return option.name === value1.name;
            }
            return false;
          }}
          hideClearable={false}
          renderOption={(renderprops, option) => (
            <Box
              component="li"
              {...withoutKey(renderprops)}
              key={option.id}
              className={`${renderprops.className} !block py-2 px-10`}
            >
              <div className="font-medium">{option.name}</div>
              <div className="font-light text-10 text-gray-400">{option.email}</div>
            </Box>
          )}
          filterOptions={(options, { inputValue }) =>
            matchSorter(options, inputValue, {
              keys: ['name'],
              threshold: matchSorter.rankings.STARTS_WITH,
            })
          }
          sxStyle={autoCompleteSxStyle}
        />
      </div>
    </div>
  );
}

export function OrderTypeFilter({ filterValue, setSelectedFilterType, setSupplier, setCustomer, setSalesPerson, options, role, type, isSupplierInvoice, autoCompleteSxStyle = null}) {
  const dispatch = useDispatch();
  const { location } = window;
  const sp = new URLSearchParams(location.search);
  const clearFilterValues = () => {
    const keysToDelete = Array.from(sp.keys());
    keysToDelete.forEach(key => sp.delete(key));
  }

  
  const filterFunction = (value) => {
    setSelectedFilterType(value)
    dispatch(setCustomer(null));
    dispatch(setSalesPerson(null));
    sp.delete('sid')
    sp.delete('cid')
    sp.delete('sales_person')
    sp.delete('archive')
    if (['admin', 'staff', 'sales'].includes(role)) {
      switch (value.value) {
        case 'supplier':
          dispatch(setSupplier({ name: 'All', value: 'all', email: '', id: 'all' }));
          clearFilterValues()
          if (!isSupplierInvoice)
            sp.set('sid', 'all')
          break;
        case 'customer_archive':
          dispatch(setSupplier(null));
          clearFilterValues()
          sp.set('archive', true)
          if (type !== "invoice") {
            sp.set('cid', 'all')
            sp.set('sales_person', 'all')
          }
          break;
        case 'supplier_archive':
          dispatch(setSupplier({ name: 'All', value: 'all', email: '', id: 'all' }));
          clearFilterValues()
          if (!isSupplierInvoice) {
            sp.set('sid', 'all')
          }
          if (isSupplierInvoice) {
            sp.delete('sid', 'all')
          }
          sp.set('archive', true)
          break;
        case 'customer':
          dispatch(setSupplier(null))
          clearFilterValues()
          break;
        case 'archive_rfq':
          clearFilterValues()
          sp.set('cid', 'all')
          sp.set('sales_person', 'all')
          sp.set('archive', true)
          break;
        case 'archive_quote':
          clearFilterValues()
          sp.set('cid', 'all')
          sp.set('sales_person', 'all')
          sp.set('archive', true)
          break;
        case 'back_order':
          clearFilterValues()
          sp.set('back_order', true)
          break;
        case 'reorder':
          clearFilterValues()
          sp.set('reorder', true)
          if (!sp.has('date')) {
            sp.set('date', moment().format('YYYY-MM-DD'));
          }
          break;
        default:
          clearFilterValues()
          break;
      }
    }
    if (role === 'customer') {
      switch (value.value) {
        case 'customer_archive':
          dispatch(setSupplier(null));
          clearFilterValues()
          sp.set('archive', true)
          break;
        case 'customer':
          dispatch(setSupplier(null))
          clearFilterValues()
          break;
        case 'container_orders_view':
          clearFilterValues()
          sp.set('view_type', 'container-orders')
          break;
        case 'container_shipment_view':
          clearFilterValues()
          sp.set('view_type', 'container')
          break;
        default:
          break;
      }
    }
    // added filter value for supplier rolw to show shipment / orders view
    if (role === 'supplier'){
      switch (value.value){
         case 'inproduction_orders_view':
          clearFilterValues()
          sp.set('view_type', 'inproduction-orders')
          break;
        case 'inproduction_shipment_view':
          clearFilterValues()
          sp.set('view_type', 'in-production')
          break;
        case 'shipped_orders_view':
          clearFilterValues()
          sp.set('view_type', 'fulfilled')
          break;
        case 'shipped_shipment_view':
          clearFilterValues()
          sp.set('view_type', 'fulfilled-shipments')
          break;
        default:
          break;
      }
    }
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <div className={`${type === 'order' ? "flex flex-col mx-6" : "flex flex-row mx-6"}`}>
      <Typography className={`"text-12 md:text-14 pr-8 " ${type === 'order' ? "pt-4" : "pt-12"}`} > Type:</Typography>
      <div className="text-12 md:text-14 pr-8 font-semibold">
        <AuraAutoComplete
          inputValues={options}
          onValueChange={filterFunction}
          defaultValue={filterValue}
          optionDisplayName="name"
          isOptionEqualToValue={(option, value1) => {
            if (option && option.name && value1.name) {
              return option.name === value1.name;
            }
            return false;
          }}
          sxStyle={autoCompleteSxStyle}
        />
      </div>
    </div>
  );
}

export function IncludeInvoiceFilter({ setSelectedFilterType, filterValue, includeInvoice, setIncludeInvoice }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setIncludeInvoice(value)
    sp.set('include_invoice_status', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={includeInvoice} />} label="Include Invoiced Order" />
  )
}

export function IncludeArchiveFilter({ setSelectedFilterType, filterValue, includeArchive, setIncludeArchive, pageType }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setIncludeArchive(value)
    sp.set('include_archive', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={includeArchive} />} label={pageType === 'container'? "Include Archive Container" : "Include Archive Order"}  />
  )
}

/**
 * PGSS-5371: cancelled invoices are opt-in on the Invoice list.
 *
 * Nothing used to exclude them, so they sat in the working list alongside live invoices. The backend
 * now hides them unless `include_cancelled` is sent, matching how archived orders already behave.
 * Setting it on the URL is enough to reach the API: getParams() forwards every search param that is
 * not a pagination header straight through as a request param.
 */
/** The display name of the cancelled invoice status, as the Status dropdown sends it. */
export const CANCELLED_INVOICE_STATUS = 'Invoice Cancelled';

/**
 * PGSS-5371: does the Status column filter name the cancelled status?
 *
 * The Status dropdown narrows within the rows already in scope; the Include Cancelled checkbox is
 * what puts cancelled rows in scope at all. Left alone, the two could disagree — box unchecked and
 * the list showing nothing but cancelled invoices, because the backend used to let the status filter
 * lift the exclusion on its own. That override is gone, so the controls are kept in step here
 * instead: choosing the status ticks the box.
 *
 * Exact match, not a substring: several statuses in the same underlying category have "cancel" in
 * their name (Order Canceled among them) and must not trip this.
 */
export function statusFilterNamesCancelled(invoiceStatus) {
  if (!invoiceStatus) return false;
  const values = Array.isArray(invoiceStatus) ? invoiceStatus : String(invoiceStatus).split(',');
  return values.some(
    (value) => typeof value === 'string'
      && value.trim().toLowerCase() === CANCELLED_INVOICE_STATUS.toLowerCase()
  );
}

export function IncludeCancelledFilter({ includeCancelled, setIncludeCancelled }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setIncludeCancelled(value)
    sp.set('include_cancelled', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={includeCancelled} />} label="Include Cancelled Invoices" />
  )
}

/**
 * PGSS-5352: let a customer see their waiting-for-approval orders on the Orders screen.
 *
 * PGSS-5094 made /orders and /awaiting-approval mutually exclusive. The client asked for one place
 * to see everything, with today's list as the default — so this is opt-in and the approval tab stays.
 *
 * Setting include_waiting_approval on the URL is enough to reach the list API, because getParams()
 * forwards every non-pagination search param. The status dropdown is a separate path: it is filtered
 * client-side in ordersSlice off the excludeWaitingApproval param, which EntitiesContent stops
 * sending when this is on.
 */
export function IncludeWaitingApprovalFilter({ includeWaitingApproval, setIncludeWaitingApproval }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setIncludeWaitingApproval(value)
    sp.set('include_waiting_approval', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={includeWaitingApproval} />} label="Include Waiting for Approval" />
  )
}

export function ShowOnlyVASFilter({ showOnlyVAS, setShowOnlyVAS, setVasTypes, vasTypes = [], pageType }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);
  const showTypes = shouldShowOrderVasTypes(showOnlyVAS, pageType);

  const writeUrl = () => {
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  };

  const handleVasOrderChange = (e) => {
    const value = e.target.checked;
    setShowOnlyVAS(value)
    sp.set('vas_order', value);
    if (!value) {
      setVasTypes?.([]);
      sp.delete('vas_types');
    }
    writeUrl();
  }

  const handleTypesChange = (event) => {
    const value = event.target.value;
    const selected = isVasType(value) ? [value] : [];
    setVasTypes(selected);
    const csv = formatVasTypesParam(selected);
    if (csv) {
      sp.set('vas_types', csv);
    } else {
      sp.delete('vas_types');
    }
    writeUrl();
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <FormControlLabel
        sx={{ mr: 1, ml: 0 }}
        control={<Checkbox onChange={handleVasOrderChange} checked={showOnlyVAS} />}
        label="Only VAS"
      />
      {showTypes && (
        <Select
          displayEmpty
          variant="standard"
          disableUnderline
          value={vasTypes[0] || ''}
          onChange={handleTypesChange}
          renderValue={(value) => vasTypeFilterLabels(value ? [value] : [])}
          sx={{
            fontSize: 12,
            fontWeight: 600,
            ml: 4,
            mt: -0.5,
            minWidth: 0,
            '& .MuiSelect-select': { py: 0, pr: '18px !important' },
          }}
        >
          <MenuItem value="">All types</MenuItem>
          {VAS_TYPE_CHOICES.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {choice.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  )
}

export function IncludeActionableFilter({ actionableOnly, setActionableOnly, pageType }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setActionableOnly(value)
    sp.set('actionableOnly', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={actionableOnly} />} label="Actionable Orders" />
  )
}


export function ShowOnlyPrimaryCustomers({ showOnlyPrimaryCustomers, setShowOnlyPrimaryCustomers }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const handleChange = (e) => {
    const value = e.target.checked;
    setShowOnlyPrimaryCustomers(value)
    sp.set('primary', value);
    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <FormControlLabel control={<Checkbox onChange={handleChange} checked={showOnlyPrimaryCustomers} />} label="Only Primary Customers"  />
  )
}

export function NotificationFilter({ filterValue, setSelectedFilterType, options, role, }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  const clearFilterValues = () => {
    const keysToDelete = Array.from(sp.keys());

    keysToDelete.forEach(key => sp.delete(key));
  }

  const filterFunction = (value) => {
    setSelectedFilterType(value)
    sp.delete('notes')
    if (['admin', 'staff', 'sales'].includes(role)) {
      switch (value.value) {
        case 'customers':
          clearFilterValues()
          sp.set('notes', value.value)
          break;
        case 'suppliers':
          clearFilterValues()
          sp.set('notes', value.value)
          break;
        default:
          clearFilterValues()
          break;
      }
    }

    window.history.replaceState(null, "", sp.toString() ? `${location.pathname}?${sp.toString()}` : location.pathname);
  }

  return (
    <div className="flex flex-row mx-6">
      <Typography className="text-12 md:text-14 pr-8 pt-12"> Notes:</Typography>
      <div className="text-12 md:text-14 pr-8 font-semibold">
        <AuraAutoComplete
          inputValues={options}
          onValueChange={filterFunction}
          defaultValue={filterValue}
          optionDisplayName="name"
          isOptionEqualToValue={(option, value1) => {
            if (option && option.name && value1.name) {
              return option.name === value1.name;
            }
            return false;
          }}
        />
      </div>
    </div>
  );
}

export function DateTypeFilter({ setReorderDate, reorderDate }) {
  const { location } = window;
  const sp = new URLSearchParams(location.search);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dateParam = urlParams.get("date");
    if (dateParam) {
      setReorderDate(dateParam);
    }
  }, []);

  const handleDateChange = (date) => {
    let formatted = null;
    if (date && date !== "Invalid date") {
      formatted = moment(date).format("YYYY-MM-DD");
    }
    sp.delete("date");
    setReorderDate(formatted);
    sp.set("date", formatted);
    window.history.replaceState(
      null,
      "",
      sp.toString()
        ? `${location.pathname}?${sp.toString()}`
        : location.pathname
    );
  };

  return (
    <div className="flex flex-col md:flex-row mx-12 mt-8 md:justify-between w-full">
      <div className="flex flex-row items-center gap-12">
        {/* Order Date */}
        <Typography
          className="text-[13px] pr-8 text-indigo font-bold"
          style={{ whiteSpace: "nowrap" }}
        >
          Order Date:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="flex items-center">
            <DatePicker
              value={reorderDate || null}
              className="text-[13px] w-120"
              onChange={handleDateChange}
              maxDate={new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  className="text-[13px] w-120"
                  inputProps={{
                    ...params.inputProps,
                    placeholder: "MM-DD-YYYY",
                  }}
                />
              )}
            />
          </div>
        </LocalizationProvider>
      </div>
    </div>
  );
}

export function OrderTyepFilterFunction() {
  const dispatch = useDispatch();
  const [value, setValue] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null)
  const [inventoryOrder, setInventoryOrder] = useState(false)

  const { selectedValue } = useSelector(({ eCommerceApp }) => eCommerceApp?.orders)
  const supplierOrderType = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.supplierOrderType);
  const orderTypeLocations = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderTypeLocations);
  const selectedDrderType = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderType);
  const orderTypeLocation = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderTypeLocation);
  const orderTypeListItems = useSelector(({ eCommerceApp }) => eCommerceApp?.orders?.orderTypeListItems);

  const currentUser = useSelector(({ user }) => user);
  const { location } = window
  const { shipmentId } = useParams()

  useEffect(() => {
    if (orderTypeListItems) {
      setValue(orderTypeListItems);
    }
  }, []);


  const getDefaultValue = () => {
    let defaultValue = '';
    if (location.pathname === '/shipments/new') {
      if (supplierOrderType) {
        defaultValue = supplierOrderType;
      } else {
        defaultValue = value?.[2];
      }
    }
    else {
      defaultValue = selectedValue;
    }
    dispatch(setSupplierOrderType(defaultValue))
    return defaultValue;
  };

  const chooseOrderType = (selected) => {
    const orderType = new URLSearchParams(location.search)
    orderType.delete('o_type');
    dispatch(getOrderType(selected));

    if (!selected) {
      if (currentUser.role === 'supplier') {
        dispatch(setSupplierOrderType(null))
        getDefaultValue()
      }
      else {
        dispatch(setSelectedValue(null))
      }

    }
    if (selected && currentUser.role !== 'supplier') {
      orderType.set('o_type', selected.name)
      dispatch(setSelectedValue(selected))
      const params = {
        stage: 'order',
        order_type: selected.name
      }
    }
    if (selected && currentUser.role === 'supplier') {
      dispatch(setSupplierOrderType(selected))
      dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
      dispatch(setOrderTypeLocation(null)) // Reset location dropdown values
      setSelectedDate(null); // Reset order date
      setInventoryOrder(false); // Reset inventory order
      if (['Direct Shipment', 'Direct Pickup'].includes(selected.display_name)) {
        // Added order_type param to get location based on selected order type
        // selected.name => Direct Shipment/Direct Pickup
        dispatch(getOrderTypeLocations({ order_type: selected.name }))

      }
    }

    window.history.replaceState(null, "", orderType.toString() ? `${location.pathname}?${orderType.toString()}` : location.pathname)
  }

  const chooseOrderTypeLocation = (selected) => {
    dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
    const { display_name: displayName = null } = selectedDrderType || {};
    dispatch(setOrderTypeLocation(selected))
    const params = { direct_shipment: displayName === 'Direct Shipment' ? true : null, direct_pickup: displayName === 'Direct Pickup' ? true : null, location: selected?.value || null }
    dispatch(getAvailableOrders({ params }))
  }

  const handleDateAndInventoryOrderChangeAction = (e, mode = 'date') => {
    dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
    // eslint-disable-next-line
    let params = { order_date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null, is_inventory: inventoryOrder }
    if (mode === 'date') {
      setSelectedDate(e);
      if (e === 'Invalid date') {
        setSelectedDate(null);
      }
      params.order_date = moment(e).format('YYYY-MM-DD')
    } else {
      setInventoryOrder(e.target?.checked);
      params.is_inventory = e.target?.checked
    }

    if (params?.order_date === 'Invalid date') {
      delete params.order_date;
    }
    dispatch(getAvailableOrders({ params }))
  }

  return (
    shipmentId === 'new' ? (<div className="flex flex-row mx-12">
      <div className='flex flex-row w-full gap-20'>
        <div className="text-12 md:text-14 pr-8 font-semibold flex">
          <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Order Type:</Typography>
          <AuraAutoComplete
            placeholder="All"
            inputValues={value}
            defaultValue={getDefaultValue()}
            onValueChange={chooseOrderType}
            optionDisplayName="name"
            hideClearable={false}
            isOptionEqualToValue={(option, value1) => {
              if (option && option.name && value1) {
                return option.name === value1;
              }
              return false;
            }}

          />
        </div>

        {['Direct Pickup', 'Direct Shipment'].includes(getDefaultValue()?.display_name) && <div className="text-12 md:text-14 pr-8 font-semibold flex">
          <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Location:</Typography>
          <AuraAutoComplete
            placeholder="Select Location"
            inputValues={orderTypeLocations}
            defaultValue={orderTypeLocation}
            onValueChange={chooseOrderTypeLocation}
            optionDisplayName="name"
            hideClearable={false}
            isOptionEqualToValue={(option, value1) => {
              if (option && option.name && value1) {
                return option.name === value1;
              }
              return false;
            }}
          />
        </div>}

        {["Regular"].includes(getDefaultValue()?.display_name) &&
          <>
            <div className="text-12 md:text-14 pr-8 font-semibold flex">
              <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Order Date:</Typography>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  style={{ zIndex: 9999 }}
                  value={selectedDate}
                  onChange={(date) => handleDateAndInventoryOrderChangeAction(date, 'date')}
                  renderInput={(params) => <TextField {...params} variant="standard" inputProps={{
                    ...params.inputProps,
                    placeholder: 'DD-MM-YYYY'
                  }} />}
                  disableFuture
                />
              </LocalizationProvider>
            </div>

            <div className="text-12 md:text-14 pr-8 font-semibold flex">
              <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Inventory order :</Typography>
              <FormControlLabel control={<Checkbox onChange={(e) => handleDateAndInventoryOrderChangeAction(e, 'inventoryOrder')} checked={inventoryOrder} />} label="" />
            </div>

          </>}
      </div> </div>) : null
  );
}


export function ContainerOrderTypeFilterFunction() {
  const dispatch = useDispatch();
  const [value, setValue] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null)
  const [inventoryOrder, setInventoryOrder] = useState(false)

  const { selectedValue } = useSelector(({ eCommerceApp }) => eCommerceApp?.orders)
  const supplierOrderType = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.supplierOrderType);
  const orderTypeLocations = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderTypeLocations);
  const selectedDrderType = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderType);
  const orderTypeLocation = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.orderTypeLocation);

  const currentUser = useSelector(({ user }) => user);
  const { location } = window
  // const { shipmentId } = useParams();
  const shipmentId = 'new';

  useEffect(() => {
    dispatch(getStatuses('order_type'))
      .then((response) => {
        if(!response.error) {
          setValue(response?.payload?.data || []);
          dispatch(setOrderTypes(response?.payload?.data || []))
        }
      });
  }, [dispatch]);


  const getDefaultValue = () => {
    let defaultValue = '';
    if (location.pathname === '/shipments/new') {
      if (supplierOrderType) {
        defaultValue = supplierOrderType;
      } else {
        defaultValue = value?.[2];
      }
    }
    else {
      defaultValue = selectedValue;
    }
    dispatch(setSupplierOrderType(defaultValue))
    return defaultValue;
  };

  const chooseOrderType = (selected) => {
    const orderType = new URLSearchParams(location.search)
    orderType.delete('o_type');
    dispatch(getOrderType(selected));

    if (!selected) {
      if (currentUser.role === 'supplier') {
        dispatch(setSupplierOrderType(null))
        getDefaultValue()
      }
      else {
        dispatch(setSelectedValue(null))
      }
    }
    if (selected && currentUser.role !== 'supplier') {
      orderType.set('o_type', selected.name)
      dispatch(setSelectedValue(selected))
      const params = {
        stage: 'order',
        order_type: selected.name
      }
    }
    if (selected && currentUser.role === 'supplier') {
      dispatch(setSupplierOrderType(selected))
      dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
      dispatch(setOrderTypeLocation(null)) // Reset location dropdown values
      setSelectedDate(null); // Reset order date
      setInventoryOrder(false); // Reset inventory order
      if (['Direct Shipment', 'Direct Pickup'].includes(selected.display_name)) {
        // Added order_type param to get location based on selected order type
        // selected.name => Direct Shipment/Direct Pickup
        dispatch(getOrderTypeLocations({ order_type: selected.name }))
      }
    }
    window.history.replaceState(null, "", orderType.toString() ? `${location.pathname}?${orderType.toString()}` : location.pathname)
  }

  const chooseOrderTypeLocation = (selected) => {
    dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
    const { display_name: displayName = null } = selectedDrderType || {};
    dispatch(setOrderTypeLocation(selected))
    const params = { direct_shipment: displayName === 'Direct Shipment' ? true : null, direct_pickup: displayName === 'Direct Pickup' ? true : null, location: selected?.value || null }
    dispatch(getAvailableOrders({ params }))
  }

  const handleDateAndInventoryOrderChangeAction = (e, mode = 'date') => {
    dispatch(resetEntities()) // Reset entities after selection of Direct Shipment and Direct Pickup
    // eslint-disable-next-line
    let params = { order_date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null, is_inventory: inventoryOrder }
    if (mode === 'date') {
      setSelectedDate(e);
      if (e === 'Invalid date') {
        setSelectedDate(null);
      }
      params.order_date = moment(e).format('YYYY-MM-DD')
    } else {
      setInventoryOrder(e.target?.checked);
      params.is_inventory = e.target?.checked
    }

    if (params?.order_date === 'Invalid date') {
      delete params.order_date;
    }
    dispatch(getAvailableOrders({ params }))
  }

  return (
    shipmentId === 'new' ? (<div className="flex flex-row mx-12">
      <div className='flex flex-row w-full gap-20 display-block'>
        <div className="text-12 md:text-14 pr-8 font-semibold flex">
          <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Order Type:</Typography>
          <AuraAutoComplete
            placeholder="All"
            inputValues={value}
            defaultValue={getDefaultValue()}
            onValueChange={chooseOrderType}
            optionDisplayName="name"
            hideClearable={false}
            isOptionEqualToValue={(option, value1) => {
              if (option && option.name && value1) {
                return option.name === value1;
              }
              return false;
            }}
          />
        </div>

        {['Direct Pickup', 'Direct Shipment'].includes(getDefaultValue()?.display_name) && <div className="text-12 md:text-14 pr-8 font-semibold flex">
          <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Location:</Typography>
          <AuraAutoComplete
            placeholder="Select Location"
            inputValues={orderTypeLocations}
            defaultValue={orderTypeLocation}
            onValueChange={chooseOrderTypeLocation}
            optionDisplayName="name"
            hideClearable={false}
            isOptionEqualToValue={(option, value1) => {
              if (option && option.name && value1) {
                return option.name === value1;
              }
              return false;
            }}
          />
        </div>}

        {["Regular"].includes(getDefaultValue()?.display_name) &&
          <>
            <div className="text-12 md:text-14 pr-8 font-semibold flex">
              <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Order Date:</Typography>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  style={{ zIndex: 9999 }}
                  value={selectedDate}
                  onChange={(date) => handleDateAndInventoryOrderChangeAction(date, 'date')}
                  renderInput={(params) => <TextField {...params} variant="standard" inputProps={{
                    ...params.inputProps,
                    placeholder: 'DD-MM-YYYY'
                  }} />}
                  disableFuture
                />
              </LocalizationProvider>
            </div>

            <div className="text-12 md:text-14 pr-8 font-semibold flex">
              <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: "nowrap" }}> Inventory order :</Typography>
              <FormControlLabel control={<Checkbox onChange={(e) => handleDateAndInventoryOrderChangeAction(e, 'inventoryOrder')} checked={inventoryOrder} />} label="" />
            </div>

          </>}
      </div> </div>) : null
  );
}

export function OutboundShipmentCreationFilterFunction(props) {
  const { setCustomer, defaultCustomer, autoCompleteSxStyle = null } = props;
  const dispatch = useDispatch();
  const customers = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.customers);
  const shipmentWeightRanges = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.shipmentWeightRanges);
  const defaultWeightRange = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.weightRange);
  // PGSS-4051: the addresses come from the orders currently on screen, so the dropdown can never
  // offer one with no orders behind it.
  //
  // Uses the slice's own selector, NOT a hand-written state path. The orders are held in an entity
  // adapter -- there is no `shipmentOrder.orders` key -- so reading the path directly returned
  // undefined and the control silently never rendered. A wrong selector fails loudly at import
  // time; a wrong path fails as an absent feature.
  const customerOrders = useSelector(selectOrders);
  const shipToFilter = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.shipToFilter);
  const selectedCustomer = useSelector(({ backofficeApp }) => backofficeApp?.shipmentOrder?.customer);
  // Resolved, never used raw: a choice that no longer matches the orders on screen stops applying
  // instead of emptying the list with no control left to clear it.
  const activeShipTo = resolveActiveShipTo(customerOrders, shipToFilter, selectedCustomer);

  // A selection that stops resolving is already inert -- but it is still stored, and a later change
  // that brings that address back would silently re-apply a filter the staff member watched go
  // blank. Now that a weight change can retire an address without also clearing the customer, drop
  // the dead value so what is stored is what is on screen.
  useEffect(() => {
    if (shipToFilter && !activeShipTo) dispatch(setShipToFilter(null));
  }, [shipToFilter, activeShipTo, dispatch]);

  /**
   * PGSS-4051 (re-fail, QA point 2): a weight change used to drop the customer unconditionally, and
   * with it the chosen address -- "Ship To is refreshed/removed and the previously selected address
   * is cleared". Weight and Ship to are different questions; answering one should not silently
   * discard the answer to the other, and it is the behaviour the previous round promised would stop.
   *
   * The customer is still dropped when the new weight band genuinely has nothing for them, which is
   * what the reset was guarding: the Customer list is refetched per band, and leaving a selection
   * that is no longer in it would show a customer with no orders and no way to tell why.
   *
   * Only an outright answer clears it. A failed request leaves the selection alone rather than
   * resetting the screen on a network blip.
   */
  const handleWeightChange = (value) => {
    dispatch(setWeightRange(value));
    dispatch(getCustomers({ weight_type: value })).then((action) => {
      const refreshedCustomers = Array.isArray(action?.payload) ? action.payload : null;
      if (!selectedCustomer || !refreshedCustomers) return;
      const stillAvailable = refreshedCustomers.some((option) => option?.id === selectedCustomer?.id);
      // setCustomer clears the ship-to choice too, so the two never disagree.
      if (!stillAvailable) dispatch(setCustomer(null));
    });
  };

  const handleCustomerChange = (value) => {
    dispatch(setCustomer(value));
  };

  return (
    <div className="flex flex-col sm:ml-16 md:ml-24 md:mr-12 md:flex-row">
      <div className="flex flex-row">
        {/* PGSS-5488: the band is applied to the CUSTOMER'S TOTAL, not to each order --
            `HAVING SUM(o2.weight)` grouped by customer -- and then every one of that customer's
            orders is returned. Labelled "Weight" above a list of orders it read as an order filter,
            so asking for 2500-10000 and getting 31 orders of 220 each looked broken. It is not: it
            answers "which customers have roughly this much waiting", which is how you decide who is
            worth a truck. The label now says so. */}
        <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: 'nowrap' }}>
            Load size
        </Typography>
        <div className="text-12 md:text-14 pr-8 w-[200px] font-semibold">
          <AuraAutoComplete
            placeholder= "All"
            inputValues={shipmentWeightRanges}
            defaultValue={defaultWeightRange || shipmentWeightRanges?.[0] || null}
            hideClearable
            onValueChange={handleWeightChange}
            isOptionEqualToValue={(option, value) => option === value}
            renderOption={(renderprops, option) => (
              <Box
                component="li"
                {...withoutKey(renderprops)}
                key={option.id}
                className={`${renderprops.className} !block py-2 px-10`}
              >
                <div className="capitalize">{option}</div>
              </Box>
            )}
            sxStyle={{
              width: "180px !important",
              ".MuiInput-input": {
                padding: "0 !important",
                textTransform: 'capitalize'
              },
            }}
          />
        </div>
      </div>
      <div className="flex flex-row">
        <Typography className="text-12 md:text-14 pr-8 pt-12">
            Customer
        </Typography>
        <div className="text-12 md:text-14 pr-8 font-semibold">
          <AuraAutoComplete
            placeholder= "All Customers"
            inputValues={customers}
            onValueChange={handleCustomerChange}
            defaultValue={defaultCustomer || null}
            optionDisplayName="name"
            // isOptionEqualToValue={(option, value) => option.name === value.name}
            isOptionEqualToValue={(option, value1) => {
              if (option && option.name && value1.name) {
                return option.name === value1.name;
              }
              return false;
            }}
            hideClearable={false}
            renderOption={(renderprops, option) => (
              <Box
                component="li"
                {...withoutKey(renderprops)}
                key={option.id}
                className={`${renderprops.className} !block py-2 px-10`}
              >
                <div className="font-medium">{option.name}</div>
                <div className="font-light text-10 text-gray-400">
                  {option.email}
                </div>
              </Box>
            )}
            filterOptions={(options, { inputValue }) =>
              matchSorter(options, inputValue, {
                keys: ["name"],
                threshold: matchSorter.rankings.STARTS_WITH,
              })
            }
            sxStyle={autoCompleteSxStyle}
          />
        </div>
      </div>

      {/* PGSS-4051: beside Customer, where the ticket asks for it and where staff already are.
          Rendered once a customer is chosen and their orders carry an address -- before that the
          page holds every customer's orders, and the addresses of customers nobody selected are
          not a ship-to filter for this one. */}
      {shouldOfferShipToFilter(customerOrders, selectedCustomer) && (
        <div className="flex flex-row">
          <Typography className="text-12 md:text-14 pr-8 pt-12" style={{ whiteSpace: 'nowrap' }}>
            Ship to
          </Typography>
          <div className="text-12 md:text-14 pr-8 font-semibold">
            <AuraAutoComplete
              placeholder="All locations"
              inputValues={shipToOptions(customerOrders)}
              defaultValue={activeShipTo}
              onValueChange={(selected) => dispatch(setShipToFilter(selected?.name || null))}
              optionDisplayName="name"
              hideClearable={false}
              isOptionEqualToValue={(option, value1) => {
                if (option && option.name && value1) {
                  return option.name === value1;
                }
                return false;
              }}
              sxStyle={autoCompleteSxStyle}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(CustomerFilter);
